// Partner companies and their tariff cards (commission per service, doc 19.1).
import { api } from "../lib/api";
import { SERVICE_CATEGORIES } from "../lib/catalog";
import { fmt, t } from "../lib/i18n";
import { h, invalidate, local, resetLocal, resource } from "../lib/ui";
import { act, Async, Button, centsToInput, Chip, Dialog, Field, formData, isBusy, openDialog, Select, toCents } from "../shared/components";
import { Icon } from "../shared/icons";
import { PageHead } from "./app";

interface Svc { service: string; commission_rate: number; pricing: "hourly" | "flat"; rate_cents: number | null }

function partnerDialog(p: any | null, defaultRate: number) {
  resetLocal(`screen:partner.${p?.id ?? "new"}`);
  const st = local(`screen:partner.${p?.id ?? "new"}`, () => ({
    services: (p?.services ?? []).map((x: any) => ({ ...x, commission_rate: Number(x.commission_rate) })) as Svc[],
  }));
  const setSvc = (i: number, patch: Partial<Svc>) => st.set({ services: st.services.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  openDialog((close) => (
    <Dialog title={p ? p.name : t("admin.partners.newTitle")} onClose={close} wide>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        const body = {
          name: d.name, contact_name: d.contact_name, email: d.email, phone: d.phone, wcb_number: d.wcb_number,
          insurance_expiry: d.insurance_expiry, status: d.status, notes: d.notes, services: st.services,
        };
        act("partner.save", async () => {
          if (p) await api.patch(`/api/admin/partners/${p.id}`, body);
          else await api.post("/api/admin/partners", body);
          close();
          invalidate("admin.partners");
        }, t("common.saved"));
      }}>
        <div class="cols-2">
          <Field label={t("admin.partners.company")} id="p-name"><input class="input" id="p-name" name="name" required value={p?.name ?? ""} /></Field>
          <Field label={t("admin.partners.contact")} id="p-contact"><input class="input" id="p-contact" name="contact_name" value={p?.contact_name ?? ""} /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("fields.email")} id="p-email"><input class="input" id="p-email" name="email" type="email" value={p?.email ?? ""} /></Field>
          <Field label={t("fields.phone")} id="p-phone"><input class="input" id="p-phone" name="phone" type="tel" value={p?.phone ?? ""} /></Field>
        </div>
        <div class="cols-2">
          <Field label={t("admin.partners.wcb")} id="p-wcb" hint={t("admin.partners.wcbHint")}><input class="input" id="p-wcb" name="wcb_number" value={p?.wcb_number ?? ""} /></Field>
          <Field label={t("admin.partners.insuranceExpiry")} id="p-ins"><input class="input" id="p-ins" name="insurance_expiry" type="date" value={p?.insurance_expiry ?? ""} /></Field>
        </div>
        <Field label={t("fields.status")} id="p-st"><Select id="p-st" name="status" value={p?.status ?? "active"} options={[{ value: "active", label: t("partnerStatus.active") }, { value: "inactive", label: t("partnerStatus.inactive") }]} /></Field>

        <div class="stack tight">
          <div class="row between">
            <span class="label">{t("admin.partners.tariff")}</span>
            <Button kind="ghost" size="small" icon="plus" onClick={() => {
              const used = new Set(st.services.map((x) => x.service));
              const next = SERVICE_CATEGORIES.find((x) => !used.has(x));
              if (next) st.set({ services: [...st.services, { service: next, commission_rate: defaultRate, pricing: "hourly", rate_cents: null }] });
            }}>{t("admin.partners.addService")}</Button>
          </div>
          {st.services.length ? (
            <div class="table-wrap">
              <table class="table">
                <thead><tr><th>{t("fields.service")}</th><th>{t("admin.partners.commission")}</th><th>{t("admin.partners.pricing")}</th><th>{t("admin.partners.rate")}</th><th /></tr></thead>
                <tbody>
                  {st.services.map((x, i) => (
                    <tr key={i}>
                      <td><Select value={x.service} onChange={(v) => setSvc(i, { service: v })} options={SERVICE_CATEGORIES.map((c) => ({ value: c, label: t(`service.${c}`) }))} /></td>
                      <td style="width:120px"><div class="input-affix"><input class="input tabular" inputMode="decimal" value={String(x.commission_rate)} aria-label={t("admin.partners.commission")}
                        onInput={(e: Event) => { const n = Number((e.target as HTMLInputElement).value); if (Number.isFinite(n) && n >= 0 && n <= 100) setSvc(i, { commission_rate: n }); }} /><span class="affix">%</span></div></td>
                      <td style="width:140px"><Select value={x.pricing} onChange={(v) => setSvc(i, { pricing: v as any })} options={[{ value: "hourly", label: t("admin.partners.hourly") }, { value: "flat", label: t("admin.partners.flat") }]} /></td>
                      <td style="width:130px"><div class="input-affix"><span class="affix">$</span><input class="input tabular" inputMode="decimal" value={centsToInput(x.rate_cents)} aria-label={t("admin.partners.rate")}
                        onInput={(e: Event) => setSvc(i, { rate_cents: toCents((e.target as HTMLInputElement).value) })} /></div></td>
                      <td style="width:40px"><button type="button" class="icon-btn" aria-label={t("common.remove")} onClick={() => st.set({ services: st.services.filter((_, j) => j !== i) })}><Icon name="x" size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p class="small muted">{t("admin.partners.noServices")}</p>}
          <span class="xs muted">{t("admin.partners.commissionNote", { rate: defaultRate })}</span>
        </div>
        <Field label={t("fields.notes")} id="p-notes"><textarea class="textarea" id="p-notes" name="notes" value={p?.notes ?? ""} /></Field>
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("partner.save")}>{p ? t("common.save") : t("admin.partners.add")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

export function PartnersPage() {
  const r = resource("admin.partners", () => api.get("/api/admin/partners"));
  const refs = resource("admin.refs", () => api.get("/api/admin/refs"));
  const defaultRate = refs.data?.commissionDefault ?? 20;
  const expiring = (d: string | null) => d && new Date(d).getTime() - Date.now() < 45 * 86400 * 1000;
  return (
    <div class="stack loose">
      <PageHead title={t("admin.partners.title")} intro={t("admin.partners.intro")}
        actions={<Button icon="plus" onClick={() => partnerDialog(null, defaultRate)}>{t("admin.partners.add")}</Button>} />
      <section class="panel">
        {Async(r, (rows: any[]) => rows.length ? (
          <div class="table-wrap">
            <table class="table">
              <thead><tr><th>{t("admin.partners.company")}</th><th>{t("admin.partners.tariff")}</th><th>{t("admin.partners.insuranceExpiry")}</th><th class="right">{t("admin.partners.jobs")}</th><th>{t("fields.status")}</th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr class="link" onClick={() => partnerDialog(p, defaultRate)}>
                    <td><span class="strong">{p.name}</span><span class="sub">{[p.contact_name, p.phone].filter(Boolean).join(" · ") || "—"}</span></td>
                    <td><div class="row" style="gap:6px">{p.services.map((s: any) => <Chip kind="info" plain>{t(`service.${s.service}`)} {fmt.number(Number(s.commission_rate), 2)}%</Chip>)}</div></td>
                    <td>{p.insurance_expiry ? <span style={expiring(p.insurance_expiry) ? "color:var(--c-critical);font-weight:600" : ""}>{fmt.date(p.insurance_expiry)}</span> : <span class="muted">{t("admin.partners.missing")}</span>}</td>
                    <td class="right tabular">{p.jobs}</td>
                    <td><Chip kind={p.status === "active" ? "good" : "neutral"}>{t(`partnerStatus.${p.status}`)}</Chip></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div class="empty"><strong>{t("admin.partners.emptyTitle")}</strong><span>{t("admin.partners.emptyBody")}</span></div>)}
      </section>
    </div>
  );
}
